import type { ClickStreamEvent, EventSourceContext } from './clickStreamEvent';
import {
  EventSources,
  makeEventName,
  recordMoMetricsEvent,
  recordMoMetricsEventImmediately
} from './moMetrics';

enum EventActions {
  Abandoned = 'Abandoned',
  Changed = 'changed',
  Clicked = 'clicked',
  Completed = 'Completed',
  ExitedEarly = 'exitedEarly',
  Failed = 'failed',
  Launched = 'Launched',
  Redirected = 'redirected',
  Rejected = 'rejected',
  Rendered = 'component_rendered',
  Shown = 'Shown',
  Uploaded = 'uploaded',
  UploadedAllRequiredDamagePhotos = 'uploadedAllRequiredDamagePhotos',
  UploadedFirstDamagePhoto = 'uploadedFirstDamagePhoto',
  Visited = 'page_visited'
}

type EventType =
  | 'Changed'
  | 'Clicked'
  | 'Visited'
  | 'Rendered'
  | 'Uploaded'
  | 'UploadedFirstDamagePhoto'
  | 'UploadedAllRequiredDamagePhotos'
  | 'Completed'
  | 'ExitedEarly'
  | 'Redirected';

enum EventNames {
  Abandoned = 'Abandoned',
  Changed = 'Changed',
  Clicked = 'Clicked',
  Completed = 'Completed',
  ExitedEarly = 'ExitedEarly',
  Failed = 'Failed',
  Launched = 'Launched',
  Redirected = 'Redirected',
  Rejected = 'Rejected',
  Rendered = 'Rendered',
  Shown = 'Shown',
  Uploaded = 'Uploaded',
  UploadedAllRequiredDamagePhotos = 'UploadedAllRequiredDamagePhotos',
  UploadedFirstDamagePhoto = 'UploadedFirstDamagePhoto',
  Visited = 'Visited'
}

const makeEvent = (
  eventName: string,
  details: Partial<EventSourceContext>
): EventSourceContext => ({
  eventName: makeEventName(eventName),
  eventTime: new Date(),
  ...details
});

const uiAttributes = (additionalEventData: any = null) => ({
  ...additionalEventData
});

export type RecordPageVisit = (
  pageName: string,
  component: string,
  eventVisitedData?: object
) => void;

export type RecordComponentRendered = (component: string, eventVisitedData?: object) => void;

export type RecordAction = (
  pageName: string,
  component: string,
  eventType: EventType,
  eventData: any
) => void;

const createEvent = (
  component: string,
  pageName: string,
  eventType: EventType
): EventSourceContext => {
  const eventName = component ? `${pageName}:${component}` : pageName;
  return makeEvent(`${eventName} - ${EventNames[eventType]}`, {
    eventAction: EventActions[eventType],
    eventSourceName: component ? `Component:${component}` : `Page:${pageName}`,
    eventSource: EventSources.UserInteraction
  });
};

const recordAction = (
  pageName: string,
  component: string,
  eventType: EventType,
  eventData?: Record<string, unknown>
) => {
  const event = createEvent(component, pageName, eventType);
  recordMoMetricsEvent(event, uiAttributes(eventData));
};

const recordActionImmediately = (
  pageName: string,
  component: string,
  eventType: EventType,
  eventData?: Record<string, unknown>
) => {
  const event = createEvent(component, pageName, eventType);

  return recordMoMetricsEventImmediately(event, uiAttributes(eventData));
};

export type RecordActionClicked = (
  pageName: string,
  component: string,
  eventClickedData: any
) => void;

export type RecordCustomEventImmediately = (
  eventName: string,
  eventData: object
) => Promise<ClickStreamEvent>;

const recordError = (error: string, errorInfo: string) => _ => {
  recordMoMetricsEvent(
    makeEvent('Error', {
      eventSourceName: 'JavascriptError'
    }),
    uiAttributes({ error, errorInfo })
  );
};

export { EventType, recordError, recordAction, recordActionImmediately };
